import { useCanManageCompany } from "@/hooks/useCanManageCompany";
import { CompanyUsersTable } from "@/components/users/CompanyUsersTable";
import { useState, useEffect, useCallback } from "react";
import { Building2, UserPlus, Trash2, Plus, Tag, UserCheck, Key } from "lucide-react";
import { useIsSuperadmin } from "@/hooks/useUserRole";
import { Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { SettingsBackLink } from "@/components/settings/SettingsBackLink";
import { RemoveFromCompanyDialog } from "@/components/users/RemoveFromCompanyDialog";
import { adminFunctionError } from "@/lib/adminFunctionError";

interface CompanyUser {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string | null;
}

interface Company {
  id: string;
  name: string;
  created_at: string;
}

interface SystemRole {
  user_id: string;
  role: string;
}

interface CustomRole {
  id: string;
  name: string;
  description: string | null;
}

interface UserCustomRole {
  user_id: string;
  role_id: string;
}

export default function CompanyAdmin({ fixedCompanyId, fixedCompanyName }: { fixedCompanyId?: string; fixedCompanyName?: string } = {}) {
  const { t } = useTranslation("admin");
  const { allowed: isAdmin, isLoading: authLoading } = useCanManageCompany();
  const { isSuperadmin: hasSuperadminRole } = useIsSuperadmin();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const activeCompanyId = fixedCompanyId ?? selectedCompanyId;
  const [companyUsers, setCompanyUsers] = useState<CompanyUser[]>([]);
  // Per-company role (user_companies.role) for the selected company — source of truth for admin/user here.
  const [companyRoles, setCompanyRoles] = useState<Record<string, "admin" | "user">>({});
  const [customRoles, setCustomRoles] = useState<CustomRole[]>([]);
  const [userCustomRoles, setUserCustomRoles] = useState<UserCustomRole[]>([]);
  const [resettingUserId, setResettingUserId] = useState<string | null>(null);

  // Remove-from-company confirmation modal
  const [removeTarget, setRemoveTarget] = useState<{ id: string; email: string } | null>(null);
  const [removingUser, setRemovingUser] = useState(false);
  const [isSuperadmin, setIsSuperadmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Dialogs
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);

  const [showCreateCompany, setShowCreateCompany] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState("");
  const [creatingCompany, setCreatingCompany] = useState(false);

  const [showAssignRole, setShowAssignRole] = useState(false);
  const [assignUserId, setAssignUserId] = useState("");
  const [selectedSystemRoles, setSelectedSystemRoles] = useState<string[]>([]);
  const [selectedCustomRoleIds, setSelectedCustomRoleIds] = useState<string[]>([]);

  // Assign existing user to company
  const [showAssignUser, setShowAssignUser] = useState(false);
  const [allUsers, setAllUsers] = useState<CompanyUser[]>([]);
  const [assignUserSearch, setAssignUserSearch] = useState("");
  const [assigningUser, setAssigningUser] = useState(false);

  const fetchCompanies = useCallback(async () => {
    const { data } = await supabase.from("companies").select("*").order("name");
    setCompanies((data as Company[]) || []);
    return data || [];
  }, []);

  const fetchCompanyUsers = useCallback(async (companyId: string) => {
    try {
      const { data, error } = await supabase.functions.invoke("admin-users", {
        body: { action: "list_company_users", companyId },
      });
      if (error) throw error;
      setCompanyUsers(data.users || []);
    } catch {
      setCompanyUsers([]);
    }
  }, []);

  const fetchCompanyRoles = useCallback(async (companyId: string) => {
    const { data } = await supabase
      .from("user_companies")
      .select("user_id, role")
      .eq("company_id", companyId);
    const map: Record<string, "admin" | "user"> = {};
    (data || []).forEach((r: any) => { map[r.user_id] = r.role === "admin" ? "admin" : "user"; });
    setCompanyRoles(map);
  }, []);

  const fetchRoles = useCallback(async () => {
    const [{ data: cRoles }, { data: ucRoles }] = await Promise.all([
      supabase.from("custom_roles").select("*").order("name"),
      supabase.from("user_custom_roles").select("*"),
    ]);
    setCustomRoles((cRoles as CustomRole[]) || []);
    setUserCustomRoles((ucRoles as UserCustomRole[]) || []);
  }, []);

  const checkSuperadmin = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase.rpc("has_custom_role", {
      _user_id: user.id,
      _role_name: "superadmin",
    });
    setIsSuperadmin(data === true);
  }, [user]);

  const init = useCallback(async () => {
    setIsLoading(true);
    await Promise.all([fetchCompanies(), fetchRoles(), checkSuperadmin()]);
    setIsLoading(false);
  }, [fetchCompanies, fetchRoles, checkSuperadmin]);

  useEffect(() => {
    // Access is gated by <CompanyAdminRoute> in App.tsx.
    init();
  }, [init]);

  // Auto-select user's company
  useEffect(() => {
    if (fixedCompanyId || !user || companies.length === 0 || selectedCompanyId) return;
    (async () => {
      const { data } = await supabase
        .from("user_companies")
        .select("company_id")
        .eq("user_id", user.id)
        .limit(1)
        .maybeSingle();
      if (data?.company_id) {
        setSelectedCompanyId(data.company_id);
      } else if (companies.length > 0) {
        setSelectedCompanyId(companies[0].id);
      }
    })();
  }, [user, companies, selectedCompanyId, fixedCompanyId]);

  // Load users when company selection changes
  useEffect(() => {
    if (activeCompanyId) {
      fetchCompanyUsers(activeCompanyId);
      fetchCompanyRoles(activeCompanyId);
    }
  }, [activeCompanyId, fetchCompanyUsers, fetchCompanyRoles]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }
  // Access enforced by the route guard in App.tsx.

  const selectedCompany = companies.find((c) => c.id === activeCompanyId) ?? (fixedCompanyId ? { id: fixedCompanyId, name: fixedCompanyName ?? "Company", created_at: "" } : undefined);

  // Superadmins implicitly have company-admin rights everywhere, so we show a single
  // "superadmin" badge for them instead of a redundant/contradictory admin|user badge.
  const getUserRoles = (userId: string) => {
    const names = userCustomRoles
      .filter((r) => r.user_id === userId)
      .map((r) => customRoles.find((cr) => cr.id === r.role_id)?.name)
      .filter(Boolean) as string[];
    const isSuper = names.includes("superadmin");
    const sys = isSuper ? [] : companyRoles[userId] === "admin" ? ["admin"] : ["user"];
    const custom = names.filter((n) => n !== "superadmin");
    return { sys, custom, isSuper };
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim() || !activeCompanyId) return;
    setInviting(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-users", {
        body: { action: "invite_to_company", email: inviteEmail.trim(), companyId: activeCompanyId },
      });
      if (error) throw error;
      if (data.error) throw new Error(data.error);
      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteEmail("");
      setShowInvite(false);
      fetchCompanyUsers(activeCompanyId);
    } catch (err: any) {
      toast.error(err.message || "Failed to invite user");
    } finally {
      setInviting(false);
    }
  };

  // Confirmation happens in the RemoveFromCompanyDialog modal, not here.
  const handleRemoveUser = async () => {
    if (!activeCompanyId || !removeTarget) return;
    setRemovingUser(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-users", {
        body: { action: "remove_user_from_company", userId: removeTarget.id, companyId: activeCompanyId },
      });
      if (error) throw await adminFunctionError(error);
      if (data.error) throw new Error(data.error);
      toast.success("User removed from company");
      setRemoveTarget(null);
      fetchCompanyUsers(activeCompanyId);
    } catch (err: any) {
      toast.error(err.message || "Failed to remove user");
    } finally {
      setRemovingUser(false);
    }
  };

  // Admins never set a password directly — the user gets a secure recovery email.
  const handleSendPasswordReset = async (userId: string, email: string) => {
    if (!activeCompanyId) return;
    if (!confirm(`Send a password reset email to ${email}?`)) return;
    setResettingUserId(userId);
    try {
      const { data, error } = await supabase.functions.invoke("admin-users", {
        body: {
          action: "send_password_reset",
          userId,
          companyId: activeCompanyId,
          redirectTo: `${window.location.origin}/reset-password`,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success(`Password reset email sent to ${email}`);
    } catch (err: any) {
      toast.error(err.message || "Failed to send reset email");
    } finally {
      setResettingUserId(null);
    }
  };

  const handleCreateCompany = async () => {
    if (!newCompanyName.trim()) return;
    setCreatingCompany(true);
    try {
      const { error } = await supabase.from("companies").insert({ name: newCompanyName.trim() });
      if (error) throw error;
      toast.success(`Company "${newCompanyName}" created`);
      setNewCompanyName("");
      setShowCreateCompany(false);
      const updated = await fetchCompanies();
      if (updated.length > 0 && !activeCompanyId) {
        setSelectedCompanyId(updated[0].id);
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to create company");
    } finally {
      setCreatingCompany(false);
    }
  };

  // Per-company admin flag (user_companies.role) — what RLS actually checks via is_company_admin().
  const setCompanyRole = async (userId: string, role: "admin" | "user") => {
    if (!activeCompanyId) throw new Error("No company selected");
    const { data, error } = await supabase.functions.invoke("admin-users", {
      body: { action: "set_company_role", userId, companyId: activeCompanyId, role },
    });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
  };

  const openAssignRoles = (userId: string) => {
    setAssignUserId(userId);
    setSelectedSystemRoles([companyRoles[userId] === "admin" ? "admin" : "user"]);
    setSelectedCustomRoleIds(
      userCustomRoles
        .filter((r) => r.user_id === userId)
        .map((r) => r.role_id)
        .filter((id) => customRoles.find((cr) => cr.id === id)?.name !== "superadmin")
    );
    setShowAssignRole(true);
  };

  // Reconcile: add newly selected, remove deselected. Admin/user lives only in user_companies.role.
  const handleAssignRoles = async () => {
    if (!assignUserId || !activeCompanyId) return;
    try {
      let changes = 0;
      const wasAdmin = companyRoles[assignUserId] === "admin";
      const wantAdmin = selectedSystemRoles.includes("admin");
      if (wasAdmin !== wantAdmin) {
        await setCompanyRole(assignUserId, wantAdmin ? "admin" : "user");
        changes++;
      }
      const current = userCustomRoles
        .filter((r) => r.user_id === assignUserId)
        .map((r) => r.role_id)
        .filter((id) => customRoles.find((cr) => cr.id === id)?.name !== "superadmin");
      for (const roleId of selectedCustomRoleIds) {
        if (current.includes(roleId)) continue;
        const { error } = await supabase.from("user_custom_roles").insert({ user_id: assignUserId, role_id: roleId });
        if (error) throw error;
        changes++;
      }
      for (const roleId of current) {
        if (selectedCustomRoleIds.includes(roleId)) continue;
        const { error } = await supabase.from("user_custom_roles").delete().eq("user_id", assignUserId).eq("role_id", roleId);
        if (error) throw error;
        changes++;
      }
      if (changes === 0) toast.info("No changes");
      else toast.success("Roles updated");
      setShowAssignRole(false);
      setSelectedSystemRoles([]);
      setSelectedCustomRoleIds([]);
      fetchRoles();
      fetchCompanyRoles(activeCompanyId);
    } catch (err: any) {
      toast.error(err.message || "Failed to update roles");
      fetchRoles();
      fetchCompanyRoles(activeCompanyId);
    }
  };

  // Roles are changed only through the "Assign Roles" dialog (add + remove there).

  const fetchAllUsers = async () => {
    try {
      const { data, error } = await supabase.functions.invoke("admin-users", {
        body: { action: "list" },
      });
      if (error) throw error;
      setAllUsers(data.users || []);
    } catch {
      setAllUsers([]);
    }
  };

  const handleAssignExistingUser = async (userId: string) => {
    if (!activeCompanyId) return;
    setAssigningUser(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-users", {
        body: { action: "add_user_to_company", userId, companyId: activeCompanyId },
      });
      if (error) throw error;
      if (data.error) throw new Error(data.error);
      toast.success("User added to company");
      setShowAssignUser(false);
      setAssignUserSearch("");
      fetchCompanyUsers(activeCompanyId);
    } catch (err: any) {
      toast.error(err.message || "Failed to add user");
    } finally {
      setAssigningUser(false);
    }
  };

  const filteredAssignUsers = allUsers
    .filter((u) => !companyUsers.some((cu) => cu.id === u.id))
    .filter((u) => u.email?.toLowerCase().includes(assignUserSearch.toLowerCase()));

  return (
    <div className={fixedCompanyId ? "pt-4" : "min-h-screen bg-background pb-24"}>
      {!fixedCompanyId && <div className="px-6 pt-8 pb-6">
        <SettingsBackLink to="/platform/companies" label={t("nav.companies", { ns: "settings" })} />

        <div className="flex items-center gap-2 mb-1">
          <Building2 className="w-5 h-5 text-primary" />
          <h1 className="text-2xl font-bold text-foreground">{t("company.title")}</h1>
        </div>
        <p className="text-muted-foreground">{t("company.subtitle")}</p>
      </div>}
      <div className={fixedCompanyId ? "space-y-4" : "px-6 space-y-4"}>
        {/* Company selector */}
        {!fixedCompanyId && <div className="flex items-center gap-3">
          <div className="flex-1">
            <SearchableSelect
              value={activeCompanyId || null}
              onChange={(val) => setSelectedCompanyId(val ?? "")}
              placeholder="Select a company"
              searchPlaceholder="Search companies…"
              emptyMessage="No companies found"
              items={companies.map((c) => ({ value: c.id, label: c.name }))}
            />
          </div>
        </div>}

        {/* Company users */}
        {activeCompanyId && (
          <>
             <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-foreground">
                {selectedCompany?.name} — Users ({companyUsers.length})
              </h2>
            </div>
            <div className="flex items-center gap-2">
              {isSuperadmin && (
                <Button variant="outline" className="gap-2" onClick={() => { fetchAllUsers(); setShowAssignUser(true); }}>
                  <UserCheck className="w-4 h-4" />
                  Add Existing User
                </Button>
              )}
              <Button className="gap-2" onClick={() => setShowInvite(true)}>
                <UserPlus className="w-4 h-4" />
                Invite User
              </Button>
            </div>
            </div>

            <CompanyUsersTable
              storageKey="company-users-column-widths"
              users={companyUsers.map((u) => ({ ...u, roles: getUserRoles(u.id) }))}
              loading={isLoading}
              empty="No users in this company yet. Invite someone to get started."
              canRemoveSuper={hasSuperadminRole}
              resettingUserId={resettingUserId}
              onAssignRoles={openAssignRoles}
              onSendReset={handleSendPasswordReset}
              onRemove={setRemoveTarget}
            />
          </>
        )}
      </div>

      {/* Invite Dialog */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite User to {selectedCompany?.name}</DialogTitle>
          </DialogHeader>
          <Input
            placeholder="Email address"
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvite(false)}>Cancel</Button>
            <Button onClick={handleInvite} disabled={inviting || !inviteEmail.trim()}>
              {inviting ? "Sending..." : "Send Invitation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Company Dialog */}
      <Dialog open={showCreateCompany} onOpenChange={setShowCreateCompany}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Company</DialogTitle>
          </DialogHeader>
          <Input
            placeholder="Company name"
            value={newCompanyName}
            onChange={(e) => setNewCompanyName(e.target.value)}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateCompany(false)}>Cancel</Button>
            <Button onClick={handleCreateCompany} disabled={creatingCompany || !newCompanyName.trim()}>
              {creatingCompany ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Role Dialog */}
      <Dialog open={showAssignRole} onOpenChange={setShowAssignRole}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Roles</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Assigning to: <strong>{companyUsers.find((u) => u.id === assignUserId)?.email}</strong>
            </p>

            {assignUserId && getUserRoles(assignUserId).isSuper ? (
              <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm text-muted-foreground">
                This user is a platform superadmin and always has full access to every company.
                Company roles (Admin / Member) do not apply to them.
              </div>
            ) : (
              /* System Roles */
              <div>
                <p className="text-sm font-medium text-foreground mb-2">Company role</p>
                <div className="space-y-2">
                  {["admin", "user"].map((role) => {
                    const checked = selectedSystemRoles.includes(role);
                    return (
                      <label
                        key={role}
                        className={`flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                          checked ? "border-primary bg-primary/5" : "border-border hover:bg-muted/30"
                        }`}
                      >
                        <input
                          type="radio"
                          name="company-role"
                          checked={checked}
                          onChange={() => setSelectedSystemRoles([role])}
                          className="rounded border-border"
                        />
                        <span className="text-sm text-foreground">{role === "admin" ? "Admin" : "Member"}</span>
                        <span className="text-xs text-muted-foreground ml-auto">
                          {role === "admin" ? "Manages this company's settings and users" : "Works on this company's appliances"}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Custom Roles */}
            {customRoles.filter((cr) => cr.name !== "superadmin").length > 0 && (
              <div>
                <p className="text-sm font-medium text-foreground mb-2">Custom Roles</p>
                <div className="space-y-2">
                  {customRoles.filter((cr) => cr.name !== "superadmin").map((role) => {
                    const checked = selectedCustomRoleIds.includes(role.id);
                    return (
                      <label
                        key={role.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border transition-colors cursor-pointer ${
                          checked ? "border-primary bg-primary/5" : "border-border hover:bg-muted/30"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) =>
                            setSelectedCustomRoleIds((prev) =>
                              e.target.checked ? [...prev, role.id] : prev.filter((id) => id !== role.id)
                            )
                          }
                          className="rounded border-border"
                        />
                        <span className="text-sm text-foreground">{role.name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignRole(false)}>Cancel</Button>
            <Button
              onClick={handleAssignRoles}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Assign Existing User Dialog */}
      <Dialog open={showAssignUser} onOpenChange={setShowAssignUser}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Existing User to {selectedCompany?.name}</DialogTitle>
          </DialogHeader>
          <Input
            placeholder="Search by email..."
            value={assignUserSearch}
            onChange={(e) => setAssignUserSearch(e.target.value)}
          />
          <div className="max-h-64 overflow-y-auto space-y-2">
            {filteredAssignUsers.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                {allUsers.length === 0 ? "Loading users..." : "No matching users found"}
              </p>
            ) : (
              filteredAssignUsers.map((u) => (
                <div
                  key={u.id}
                  className="flex items-center justify-between bg-muted/50 rounded-lg p-3"
                >
                  <span className="text-sm text-foreground truncate">{u.email}</span>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={assigningUser}
                    onClick={() => handleAssignExistingUser(u.id)}
                  >
                    Add
                  </Button>
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignUser(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove from company confirmation */}
      <RemoveFromCompanyDialog
        open={removeTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRemoveTarget(null);
        }}
        userEmail={removeTarget?.email ?? ""}
        companyName={selectedCompany?.name ?? "this company"}
        confirming={removingUser}
        onConfirm={handleRemoveUser}
      />
    </div>
  );
}
